<script lang="ts" setup>
  import AsteriskText from '~/components/shared/Forms/AsteriskText.vue';

  interface Option {
    title: string;
    value: string;
    // When true, the option is still registered (so its label can be matched/displayed
    // as the current selected value) but is excluded from the browsable dropdown list.
    hiddenFromList?: boolean;
  }

  const props = defineProps<{
    isCompulsory?: boolean;
    labelText: string;
    value: string;
    options: Option[];
    name: string;
    rules?: any;
    isDisabled?: boolean;
    placeholder?: string;
    stateKey?: string;
    itemKey?: number;
    width?: string;
    allowClear?: boolean;
    labelIcon?: any;
    dropdownMatchSelectWidth?: boolean;
    dropdownMinWidth?: string;
    showDisabledTooltip?: boolean;
    labelWidth?: string;
  }>();
  const emit = defineEmits(['update:value']);

  const localValue = ref<string>(props.value);

  const selectedOptionTitle = computed(() => {
    if (!props.showDisabledTooltip || !localValue.value) return '';
    return props.options.find(o => String(o.value) === String(localValue.value))?.title || '';
  });

  const filterOption = (input: string, option: Option) => {
    if (option.hiddenFromList) return false;
    const searchTerms = input.toLowerCase().split(' ');
    const optionTitle = option.title.toLowerCase();
    const optionValue = String(option.value).toLowerCase();
    return searchTerms.every(term => optionValue.includes(term) || optionTitle.includes(term));
  };

  const onSelect = (selectedValue: string) => {
    localValue.value = selectedValue;
    if (props.itemKey) {
      emit('update:value', selectedValue, props.itemKey, props.stateKey);
    } else {
      emit('update:value', selectedValue, props.stateKey);
    }
  };

  // Handle clear icon only if allowClear is true
  const onClear = () => {
    if (props.allowClear) {
      localValue.value = '';
      if (props.itemKey) {
        emit('update:value', '', props.itemKey, props.stateKey);
      } else {
        emit('update:value', '', props.stateKey);
      }
    }
  };

  // watch for changes in the prop
  watch([() => props.value], ([value]) => {
    localValue.value = value;
  });
</script>

<template>
  <a-form-item
    class="!font-medium mb-3 custom-dropdown-with-search"
    :name="props.name"
    :rules="props.rules"
    :label-col="{ style: { width: props.labelWidth || '183px' } }"
  >
    <template v-if="props.isCompulsory" #label>
      <AsteriskText :label="props.labelText" />
    </template>
    <template v-else #label>
      <p class="text-zinc-600">
        {{ props.labelText }}
        <span v-if="props.labelIcon" class="inline-block align-middle ml-1">
          <component
            :is="props.labelIcon"
            title="Default bank account is auto-selected, please change it as may be required"
          />
        </span>
      </p>
    </template>
    <a-tooltip
      :title="props.showDisabledTooltip && props.isDisabled && selectedOptionTitle ? selectedOptionTitle : ''"
      placement="top"
      :mouse-enter-delay="0.3"
    >
      <a-select
        :value="localValue"
        :placeholder="props.placeholder"
        :filter-option="filterOption"
        :style="{ width: props.width || '248px' }"
        show-search
        :allow-clear="props.allowClear ?? false"
        name="donorName"
        :disabled="props.isDisabled"
        :dropdown-match-select-width="props.dropdownMatchSelectWidth ?? false"
        :dropdown-style="{
          minWidth: props.dropdownMinWidth || props.width || '248px',
          maxWidth: props.dropdownMinWidth || props.width || '248px'
        }"
        popup-class-name="custom-dropdown-with-search-popup"
        @select="onSelect"
        @clear="onClear"
      >
        <a-select-option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :title="option.title"
          :style="option.hiddenFromList ? { display: 'none' } : undefined"
        >
          {{ option.title }}
        </a-select-option>
      </a-select>
    </a-tooltip>
  </a-form-item>
</template>

<style>
  .custom-dropdown-with-search .ant-form-item-label > label {
    color: #52525b !important;
    line-height: 21px;
  }
  .custom-dropdown-with-search .ant-select-selector {
    padding: 8px !important;
    height: 39px !important;
  }

  .custom-dropdown-with-search .ant-select-selection-item {
    line-height: 21px !important;
    font-weight: 500 !important;
    color: #27272a;
  }

  .custom-dropdown-with-search .ant-select-selection-placeholder {
    line-height: 21px !important;
    font-weight: 500 !important;
  }

  /* Long option labels shouldn't force the popup wider than the field's own width
     (min-width/max-width on dropdown-style, set inline above) — truncate overflow
     with an ellipsis instead (full text is still available via :title). */
  .custom-dropdown-with-search-popup .ant-select-item-option-content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
